import { Checkbox, Chip, FormControl, Grid, InputLabel, ListItemText, MenuItem, Select, Tooltip, Typography } from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import AddIcon from '@material-ui/icons/Add'
import { models, TextButton } from '@tfo/common'
import { observer } from 'mobx-react'
import * as React from 'react'
import { useEffect, useState } from 'react'
import { useIntl } from 'react-intl'
import CompanyUserModal from '../../../components/modals/company-user-modal/CompanyUserModal'
import { useStore } from '../../../hooks/useStore'
import { LoginMethod } from '../../../models/enums/LoginMethod'
import { ICompanyUser } from '../../../models/ICompanyUser'
import $http from '../../../utils/$http'
import CustomerPortalTable, { Column } from '../common/CustomerPortalTable'

const useStyles = makeStyles({
	userList: {
		padding: '1em'
	},
	table: {
		marginTop: '3%',
		minWidth: 650,
	},
	row: {
		'&:hover': {
			cursor: 'pointer',
			background: '#e6e6e6',
		},
	},
	addUser: {
		marginTop: '4%',
		justifyContent: 'center',
		display: 'inline',
		color: '#059ac4',
		'&:hover': {
			cursor: 'pointer',
		},
	},
	resetFilter: {
		alignSelf: 'center',
		padding: '0.5rem',
		color: '#059ac4',
		marginLeft: '1rem',
		'&:hover': {
			cursor: 'pointer',
		},
	},
	scroll: {
		overflowX: 'scroll',
		scrollbarWidth: 'none', //Firefox
		overflow: '-moz-scrollbars-none',
		'&::-webkit-scrollbar': {
			display: 'none' //Chrome & Safari
		}
	},
	itemRow: {
		display: 'flex'
	},
	tfoPinkChip: {
		backgroundColor: '#ec9dc9',
		color: '#f1f1f1'
	},
	tfoPinkColor: {
		color: '#ec9dc9'
	}
})

const loginMethodTranslation = {
	[LoginMethod.Password]: 'common.loginMethod.password',
	[LoginMethod.BankId]: 'common.loginMethod.bankid',
	[LoginMethod.VerificationCode]: 'common.loginMethod.verificationcode',
	[LoginMethod.SSO]: 'common.loginMethod.sso'
}

export const UserList = observer(() => {
	const classes = useStyles()
	const intl = useIntl()
	const { clientViewStore, userStore } = useStore()
	const [isNewUser, setNewUser] = useState(false)
	const [thisUser, setThisUser] = useState<ICompanyUser>({} as ICompanyUser)
	const [users, setUsers] = React.useState([])
	const [filteredUsers, setFilteredUsers] = React.useState([])
	const [companyDictionary, setCompanyDictionary] = React.useState({})
	const [companyList, setCompanyList] = React.useState([])
	const [selectedCompanies, setSelectedCompanies] = React.useState([])
	const defaultCompany: models.ICompany = {
		name: intl.formatMessage({ id: 'client.filter.all.companies' }),
		id: -1,
		researcherId: 1,
		accountManagerId: 1,
		note: '',
		parentCompanyId: -1,
		organizationNumber: '',
		accountManagerName: 'acoountManagerFirstName',
		language: '',
		tfoAcceptsOrDeniesCandidate: false,
		subsidiaries: null,
		isSSOEnabled: false
	}


	useEffect(() => {
		getCompanyNames()
		getUsers()
	}, [])

	useEffect(() => {
		filterUserList()
	}, [selectedCompanies, users])


	//If user is passed as argument then update, otherwise create user
	const openModal = (user?: ICompanyUser) => {
		if (user) {
			setNewUser(false)
			setThisUser(users.find(x => x.email === user.email))
		} else {
			setThisUser({} as ICompanyUser)
			setNewUser(true)
		}
		clientViewStore.userModalOpen = true
	}

	const getCompanyNames = async (): Promise<void> => {
		const companyDict = {}
		const companies = await $http.get<any>(`/company/flat/${userStore.user.companyId}?subsidiaries=true`)
		sortCompanyList(companies)

		setSelectedCompanies(() => [defaultCompany])
		companies?.map((company: models.ICompany) => {
			companyDict[company.id] = company.name
		})
		setCompanyDictionary(companyDict)
	}

	const sortCompanyList = (companies: Array<models.ICompany>): void => {
		let sortedList = companies.sort((a, b) => a.name.localeCompare(b.name))
		sortedList.unshift(defaultCompany)
		setCompanyList(() => sortedList)
	}

	const getUsers = () => {
		clientViewStore.loading = true
		$http.get<ICompanyUser[]>(`/user/customer/company/${userStore.user.companyId}?includeSubsidiaries=true`).then()
			.then(users => { setUsers(users || []) })
			.finally(() => clientViewStore.loading = false)
		filterUserList()
	}

	const onUserUpdated = (): void => {
		getUsers();
	}

	const sortByCompanyThenName = (a: ICompanyUser & { label: string }, b: ICompanyUser & { label: string }): number => {
		const { label: labelA, companyId: companyIdA } = a
		const { label: labelB, companyId: companyIdB } = b

		if (companyDictionary[companyIdA] < companyDictionary[companyIdB])
			return -1
		if (companyDictionary[companyIdA] > companyDictionary[companyIdB])
			return 1
		if (labelA < labelB) return -1
		if (labelA > labelB) return 1
		return 0
	}

	const onSelectedCompanies = (event: any): void => {
		const isAllCompaniesCurrent = event.target.value.find(x => x.id === -1)
		if (event.target.value.length < 2 || !isAllCompaniesCurrent) {
			setSelectedCompanies(() => event.target.value)
			return
		}
		const allCompaniesPrev = selectedCompanies.find(x => x.id === -1)
		if (allCompaniesPrev) {
			const defaultAll = event.target.value.find(x => x.id === -1)
			const index = event.target.value.indexOf(defaultAll)
			event.target.value.splice(index, 1)
			setSelectedCompanies(() => event.target.value)
		} else {
			setSelectedCompanies(() => [defaultCompany])
		}
	}

	const filterUserList = (): void => {
		const userList = selectedCompanies.find(s => s.id === -1) ? users : users.filter(u => selectedCompanies.find(c => c.id === u.companyId))
		setFilteredUsers(() => userList)
	}

	const onResetFilter = (): void => {
		setSelectedCompanies(() => [defaultCompany])
	}

	const usersToDisplay = (filteredUsers || [])
		.map(u => { return { ...u, label: `${u.firstName} ${u.lastName}` } })
		.filter(u => u.isActive)
		.sort(sortByCompanyThenName)

	const getColumns: Column[] = [
		{ label: 'company', prop: 'companyName' }, 
		{ label: 'name', prop: 'label' }, 
		{ label: 'phonenumber', prop: 'phoneNumber', type: 'number', hideOnXS: true }, 
		{ label: 'email', prop: 'email', hideOnXS: true }, 
		{ label: 'role', prop: 'role', hideOnXS: true }, 
		{ label: 'loginMethod', prop: 'loginMethodLabel', hideOnXS: true }
	]

	const getRows = usersToDisplay.map(x => ({
		companyName: companyDictionary?.[x.companyId],
		label: x.label,
		phoneNumber: x.phoneNumber,
		email: x.email,
		role: intl.formatMessage({ id: `companyUserRoles.${x.roles[0]?.role}` }),
		loginMethodLabel: intl.formatMessage({ id: loginMethodTranslation[x.loginType] })
	}))

	return (

		<Grid container direction="column" className={classes.userList}>
			<Grid container>

				<Grid item xs={6} className={classes.itemRow}>
					<FormControl style={{ width: '20em' }} variant="outlined">
						<InputLabel id="company-label" >
							{intl.formatMessage({ id: 'controlpoint.company' })}
						</InputLabel>
						<Select
							id="company-select"
							multiple
							labelId="company-label"
							value={selectedCompanies}
							onChange={event =>
								onSelectedCompanies(event)
							}
							label={intl.formatMessage({ id: 'controlpoint.company' })}
							variant="outlined"
							renderValue={
								() => {
									return <>
										{
											selectedCompanies.map(c => (
												<Chip key={c.id} className={classes.tfoPinkChip} label={c.name} />
											))
										}
									</>
								}
							}
						>
							{companyList.map(
								company => (
									<MenuItem key={company.id} value={company}>
										<Checkbox style={{ color: '#ec9dc9' }} checked={selectedCompanies.filter(x => x.id === company.id).length > 0} />
										<ListItemText primary={company.name} />
									</MenuItem>
								),
							)}
						</Select>
					</FormControl>
					<div className={classes.resetFilter}>
						<Typography onClick={() => onResetFilter()}>
							{intl.formatMessage({ id: 'client.filter.reset.companies' })}
						</Typography>

					</div>
				</Grid>

				<Grid item xs={6}>
					<div style={{ display: 'flex', justifyContent: "flex-end" }}>
						<Tooltip 
							title={clientViewStore.company?.isSSOEnabled ? intl.formatMessage({ id: 'userManagement.sso.disabled.message' }) : ''}
							placement="bottom">
							<span>
								<TextButton 
									blue 
									small 
									disabled={clientViewStore.company?.isSSOEnabled}
									onClick={() => openModal()} 
									icon={<AddIcon />}>
									{intl.formatMessage({ id: 'client.create.newuser' })}
								</TextButton>
							</span>
						</Tooltip>
					</div>
				</Grid>

			</Grid>

			<Grid item xs={12}>
				<CustomerPortalTable columns={getColumns} rows={getRows} onRowClick={(user) => openModal(user)} />
			</Grid>
			{clientViewStore.userModalOpen && (
				<CompanyUserModal
					data={thisUser as ICompanyUser}
					newUser={isNewUser}
					companyUsers={users}
					onUserChanged={onUserUpdated}
					onCloseClick={() => (clientViewStore.userModalOpen = false)}
				/>
			)}
		</Grid>
	)
})

export default UserList
